import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import config from "../../config";

const parseSelectedFeatures = (value) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString();
};

const getStatusBadge = (status) => {
  if (status === "pending") {
    return (
      <span className="badge bg-warning-100 text-warning-600 px-3 py-2 rounded-pill fw-medium">
        Pending
      </span>
    );
  }
  if (status === "accepted") {
    return (
      <span className="badge bg-success-100 text-success-600 px-3 py-2 rounded-pill fw-medium">
        Accepted
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="badge bg-danger-100 text-danger-600 px-3 py-2 rounded-pill fw-medium">
        Rejected
      </span>
    );
  }
  return (
    <span className="badge bg-secondary-100 text-secondary-600 px-3 py-2 rounded-pill fw-medium">
      {status || "-"}
    </span>
  );
};

const ReservationsTable = () => {
  const { auth, user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  const isAdminView = useMemo(() => {
    if (!user) return false;
    const perms = Array.isArray(user.permissions) ? user.permissions : [];
    return user.role === "admin" || perms.includes("admin_access");
  }, [user]);

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const basePath = isAdminView ? "/api/reservations" : "/api/reservations/my";
      const url = new URL(`${config.API_BASE_URL}${basePath}`);
      if (filterStatus !== "all") url.searchParams.set("status", filterStatus);

      const response = await auth.fetch(url.toString());
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to fetch reservations");
      }
      const data = await response.json();
      setReservations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching reservations:", err);
      toast.error("Failed to load reservations");
    } finally {
      setLoading(false);
    }
  }, [auth, filterStatus, isAdminView]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const updateStatus = async (reservationId, newStatus) => {
    try {
      const response = await auth.fetch(
        `${config.API_BASE_URL}/api/reservations/${reservationId}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus })
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to update status");
      }

      setReservations((prev) =>
        prev.map((r) => (r.id === reservationId ? { ...r, status: newStatus } : r))
      );
      toast.success(`Reservation marked as ${newStatus}`);
    } catch (err) {
      console.error("Error updating reservation status:", err);
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <button
          className="btn btn-primary-600 d-flex align-items-center gap-2"
          onClick={fetchReservations}
        >
          <Icon icon="mdi:refresh" className="text-xl" />
          Refresh
        </button>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table hover-table">
            <thead>
              <tr>
                <th scope="col">Status</th>
                <th scope="col">Date</th>
                <th scope="col">Venue</th>
                <th scope="col">Customer</th>
                <th scope="col">Selected Offers</th>
                <th scope="col">Message</th>
                <th scope="col" className="text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : reservations.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">
                    <Icon
                      icon="mdi:calendar-remove-outline"
                      className="text-6xl text-secondary-light mb-3 opacity-50"
                    />
                    <p className="text-secondary-light">No reservations found</p>
                  </td>
                </tr>
              ) : (
                reservations.map((r) => {
                  const features = parseSelectedFeatures(r.selected_features);
                  const featuresLabel =
                    features.length === 0
                      ? "-"
                      : features
                          .slice(0, 3)
                          .map((f) => f?.name || f?.title || `#${f?.id || ""}`)
                          .filter(Boolean)
                          .join(", ");

                  return (
                    <tr key={r.id}>
                      <td>{getStatusBadge(r.status)}</td>
                      <td>{formatDate(r.created_at)}</td>
                      <td>{r.venue_name || "-"}</td>
                      <td>
                        <div className="d-flex flex-column">
                          <span className="fw-medium text-primary-600">
                            {r.customer_name || "-"}
                          </span>
                          <span className="text-sm text-secondary-light">
                            {r.customer_email || "-"}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="text-secondary-light">{featuresLabel}</span>
                        {features.length > 3 && (
                          <span className="text-secondary-light"> +{features.length - 3}</span>
                        )}
                      </td>
                      <td style={{ maxWidth: "280px", whiteSpace: "pre-wrap" }}>
                        <span className="text-secondary-light">
                          {r.customer_message || "-"}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          {r.status !== "accepted" && (
                            <button
                              className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              onClick={() => updateStatus(r.id, "accepted")}
                              title="Accept"
                            >
                              <Icon icon="mdi:check" />
                            </button>
                          )}
                          {r.status !== "rejected" && (
                            <button
                              className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              onClick={() => updateStatus(r.id, "rejected")}
                              title="Reject"
                            >
                              <Icon icon="mdi:close" />
                            </button>
                          )}
                          {r.status !== "pending" && (
                            <button
                              className="w-32-px h-32-px bg-warning-focus text-warning-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              onClick={() => updateStatus(r.id, "pending")}
                              title="Back to Pending"
                            >
                              <Icon icon="mdi:clock-outline" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ReservationsTable;
